import $ from "jquery";
import isoFetch from "isomorphic-fetch";
import { triggerAuthRequired, isAuthRequiredTriggered } from "../_editor/utils/authRequired";
import { triggerServerErrorToast } from "../_editor/utils/serverErrorToast";
import { getToken, sanitizeBaseUrl, assetModeToken } from "./environment_config";

// Safety net for any $.ajax call to our API that bypasses apiAjax below. As of
// 2026-10 there are none: server_utils.es6 goes through apiAjax/apiFetch
// (which renew the session AND re-send on a 401), and the remaining direct
// jQuery calls hit third-party hosts (Flickr, PhET). A new direct call would
// get the silent renewal here but NOT the re-send — use apiAjax instead.
// Gated to our own API origins via isOwnApiUrl so a third-party 401/403 can't
// false-trigger the sign-in popup.
if (typeof document !== "undefined") {
  $(document).ajaxError((event, jqXHR, ajaxSettings) => {
    if (
      (jqXHR.status === 401 || jqXHR.status === 403) &&
      isOwnApiUrl(ajaxSettings && ajaxSettings.url)
    ) {
      triggerAuthRequired();
    }
    if (
      (jqXHR.status === 404 || jqXHR.status >= 500) &&
      isOwnApiUrl(ajaxSettings && ajaxSettings.url)
    ) {
      triggerServerErrorToast(jqXHR.status);
    }
  });
}

// Our own middleware's origin, used to gate the 401/403 popup in authedFetch
// below so a 403 from an unrelated host (Azure blob / a signed URL) can't
// false-trigger a "session expired" nag. Mirrors the baseUrl normalization
// server_utils.es6 uses everywhere: window.ideaLibraryRootUrl may already be
// absolute or may be a path relative to the current origin.
function ownApiOrigin() {
  let base = (typeof window !== "undefined" && window.ideaLibraryRootUrl) || "";
  if (base && !base.toLowerCase().startsWith("http")) {
    base = window.location.origin + base;
  }
  try {
    return base ? new URL(base).origin : window.location.origin;
  } catch (e) {
    return window.location.origin;
  }
}

function getOwnApiOrigins() {
  const origins = new Set();

  // Add ideaLibraryRootUrl origin
  let base = (typeof window !== "undefined" && window.ideaLibraryRootUrl) || "";
  if (base && !base.toLowerCase().startsWith("http")) {
    base = window.location.origin + base;
  }
  try {
    origins.add(base ? new URL(base).origin : window.location.origin);
  } catch (e) {
    origins.add(window.location.origin);
  }

  // Add MW_API_URL origin if defined (different modules build URLs from different constants)
  if (process.env.MW_API_URL) {
    let mwBase = process.env.MW_API_URL;
    if (mwBase && !mwBase.toLowerCase().startsWith("http")) {
      mwBase = window.location.origin + mwBase;
    }
    try {
      origins.add(new URL(mwBase).origin);
    } catch (e) {
      // Ignore malformed URL
    }
  }

  return origins;
}

// Resolve exactly as fetch() will (WHATWG URL against the page): regex-sniffing
// "relative" let `//evil`, `/\evil`, ` https://evil`, `ht\ttps://evil`, … through.
function resolveUrl(url) {
  try {
    return new URL(url, window.location.origin);
  } catch (e) {
    return null;
  }
}

function isOwnApiUrl(url) {
  const u = resolveUrl(url);
  // Relative URLs resolve against our own origin, which serves the app —
  // treat those as our API too.
  return !!u && (u.origin === window.location.origin || getOwnApiOrigins().has(u.origin));
}

// ?assetMode=token: tag every request to our own middleware. Covers direct
// fetch() call sites (window.fetch wrapper), the helpers below (isoFetch is
// bound to the original fetch at import) and jQuery ajax.
export function withAssetMode(url, options = {}) {
  if (!assetModeToken || !isOwnApiUrl(url)) return options;
  // [[k, v], ...] is a valid HeadersInit but spreads into index keys
  const headers = Array.isArray(options.headers)
    ? Object.fromEntries(options.headers)
    : options.headers;
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const next = new Headers(headers);
    next.set("X-Aidea-Asset-Mode", "token");
    return { ...options, headers: next };
  }
  return { ...options, headers: { ...headers, "X-Aidea-Asset-Mode": "token" } };
}

const fetch = (url, options) => isoFetch(url, withAssetMode(url, options));

if (assetModeToken && typeof window !== "undefined" && window.fetch) {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, options) =>
    typeof input === "string" || input instanceof URL
      ? nativeFetch(input, withAssetMode(String(input), options))
      : nativeFetch(input, options); // Request objects: left alone
  $.ajaxPrefilter((options, original, jqXHR) => {
    if (isOwnApiUrl(options.url)) jqXHR.setRequestHeader("X-Aidea-Asset-Mode", "token");
  });
}

// authedFetch — explicit call-site replacement for the old blanket
// window.fetch monkeypatch. Injects the bearer token and fires the
// auth-required popup on 401/403, but only for requests to our own
// middleware (see isOwnApiUrl) so a third-party 403 (e.g. polling an Azure
// blob SAS URL) can't false-trigger it. Returns the raw Response — callers
// keep their own .json()/.blob()/.ok handling.
// ── Re-send after silent renewal ────────────────────────────────────────────
// A 401 from our own middleware usually just means the access token expired
// (sleep, a frozen tab, the second it lapsed). triggerAuthRequired renews it
// silently; when that leaves a live token in place (`=== true`) the request is
// re-sent ONCE with it, so the caller sees the real result instead of a
// dropped save. Only 401 is retried: a 403 is a permission answer that a new
// token does not change.

function hasAuthHeader(headers) {
  if (!headers) return false;
  if (typeof Headers !== "undefined" && headers instanceof Headers)
    return headers.has("Authorization");
  return Object.keys(headers).some((key) => key.toLowerCase() === "authorization");
}

function withFreshAuth(headers) {
  const value = "Bearer " + getToken();
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const next = new Headers(headers);
    next.set("Authorization", value);
    return next;
  }
  const next = {};
  Object.keys(headers || {}).forEach((key) => {
    if (key.toLowerCase() !== "authorization") next[key] = headers[key];
  });
  next.Authorization = value;
  return next;
}

// Always runs the usual auth-failure handling; resolves true only when the
// caller should re-send. Non-retryable failures do not wait for the recovery.
async function renewedForRetry(status, url, canRetry = true) {
  if (!canRetry || status !== 401 || !isOwnApiUrl(url)) {
    triggerAuthRequired();
    return false;
  }
  return (await triggerAuthRequired()) === true;
}

// The Bearer goes ONLY to our API or an allowlisted MW origin: a URL from a query
// param (e.g. ?saurl=) must never receive the session token.
export function mayCarryToken(url) {
  const u = resolveUrl(url);
  if (!u) return false;
  if (u.origin === window.location.origin || getOwnApiOrigins().has(u.origin)) return true;
  return sanitizeBaseUrl(u.origin + "/") !== null;
}

// quiet: a probe whose failure is a normal answer (e.g. "no active build") — no
// server-error toast and no auth modal on 403; a 401 still gets the one renewal.
export async function authedFetch(url, options = {}, { quiet = false } = {}) {
  const withToken = mayCarryToken(url);
  const send = () =>
    fetch(url, {
      ...options,
      headers: withToken
        ? { ...options.headers, Authorization: "Bearer " + getToken() }
        : { ...options.headers },
    });
  let response = await send();
  if ((response.status === 401 || (response.status === 403 && !quiet)) && isOwnApiUrl(url)) {
    if (await renewedForRetry(response.status, url)) response = await send();
  }
  if (!quiet && (response.status === 404 || response.status >= 500) && isOwnApiUrl(url)) {
    triggerServerErrorToast(response.status);
  }
  return response;
}

export async function apiFetch(url, options = {}) {
  let response = await fetch(url, options);
  if (response.status === 401 || response.status === 403) {
    const retry = await renewedForRetry(response.status, url, hasAuthHeader(options.headers));
    if (retry) response = await fetch(url, { ...options, headers: withFreshAuth(options.headers) });
  }
  if (response.status === 401 || response.status === 403) {
    console.error("🔒 " + response.status + " detected in apiFetch - showing auth popup...");
    return Promise.reject(new Error("Unauthorized - authentication required"));
  }
  if ((response.status === 404 || response.status >= 500) && isOwnApiUrl(url)) {
    triggerServerErrorToast(response.status);
  }
  return response;
}

export async function apiAjax(ajaxOptions = {}) {
  if (typeof window.$ === "undefined" || !window.$.ajax) {
    console.error("jQuery is not available. Cannot use apiAjax.");
    return Promise.reject(new Error("jQuery is not available"));
  }

  const originalErrorHandler = ajaxOptions.error;
  // An auth failure on the FIRST attempt is held back from the caller: it may
  // still turn into a success once the request is re-sent.
  let authFailure = null;

  const errorHandler = function (jqXHR, textStatus, errorThrown) {
    if (jqXHR.status === 401 || jqXHR.status === 403) {
      console.error("🔒 " + jqXHR.status + " detected in apiAjax - recovering session...");
      authFailure = { context: this, args: [jqXHR, textStatus, errorThrown] };
      return false;
    }

    if (originalErrorHandler && typeof originalErrorHandler === "function") {
      return originalErrorHandler.call(this, jqXHR, textStatus, errorThrown);
    }
  };

  try {
    return await window.$.ajax({ ...ajaxOptions, error: errorHandler });
  } catch (err) {
    if (!authFailure) throw err;

    const status = authFailure.args[0].status;
    const retry = await renewedForRetry(
      status,
      ajaxOptions.url,
      hasAuthHeader(ajaxOptions.headers)
    );
    if (retry) {
      // Second attempt runs with the caller's own success/error handlers.
      return window.$.ajax({ ...ajaxOptions, headers: withFreshAuth(ajaxOptions.headers) });
    }

    // No retry. If the sign-in modal is up it owns the screen (as before);
    // otherwise — mw unreachable, or a 403 — hand the failure to the caller
    // so it can close its overlay and show its own message.
    if (!isAuthRequiredTriggered() && originalErrorHandler) {
      originalErrorHandler.apply(authFailure.context, authFailure.args);
    }
    throw err;
  }
}

export function apiGet(url, options = {}) {
  return apiFetch(url, {
    ...options,
    method: "GET",
  });
}

export function apiPost(url, data, options = {}) {
  return apiFetch(url, {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: typeof data === "string" ? data : JSON.stringify(data),
  });
}

export function apiPut(url, data, options = {}) {
  return apiFetch(url, {
    ...options,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: typeof data === "string" ? data : JSON.stringify(data),
  });
}

export function apiPatch(url, data, options = {}) {
  return apiFetch(url, {
    ...options,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: typeof data === "string" ? data : JSON.stringify(data),
  });
}

export function apiDelete(url, options = {}) {
  return apiFetch(url, {
    ...options,
    method: "DELETE",
  });
}
